import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  useChatRoomListQuery,
  useCreateChatRoomMutation,
  useDeleteChatRoomMutation,
  useUpdateChatRoomMutation,
} from "generated/graphql";
import { useSnackbar } from "hooks";
import { useNavigate, useParams } from "react-router";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { ChatRoomFormDialog } from "./chat-room-form-dialog";
import { ChatRoomListRow } from "./chat-room-list-row";

export const ChatRoomList: React.FC = () => {
  const routerParams = useParams();
  const chatRoomId = routerParams.chatRoomId ?? "";

  const { displaySnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { data: chatRoomListData, updateQuery } = useChatRoomListQuery();

  const [createChatRoom] = useCreateChatRoomMutation({
    onCompleted: (data) => {
      displaySnackbar("success", "Created chat room successfully");

      updateQuery((prev) => ({
        chatRoomList: {
          data: [...prev.chatRoomList.data, data.createChatRoom],
          total: prev.chatRoomList.total + 1,
        },
      }));

      navigate(`/chatroom/${data.createChatRoom.id}`);
    },
    onError: (error) => {
      displaySnackbar("error", error.message);
    },
  });

  const [updateChatRoom] = useUpdateChatRoomMutation({
    onCompleted: (data) => {
      displaySnackbar("success", "Updated chat room successfully");

      updateQuery((prev) => {
        {
          const newChatRooms = [...prev.chatRoomList.data];
          const targetChatRoom = newChatRooms.find(
            (chatRoom) => chatRoom.id === data.updateChatRoom.id,
          );

          if (targetChatRoom) {
            targetChatRoom.name = data.updateChatRoom.name;
          }

          return {
            chatRoomList: {
              data: newChatRooms,
              total: prev.chatRoomList.total,
            },
          };
        }
      });

      navigate(`/chatroom/${data.updateChatRoom.id}`);
    },
    onError: (error) => {
      displaySnackbar("error", error.message);
    },
  });

  const [deleteChatRoom] = useDeleteChatRoomMutation({
    onCompleted: (data) => {
      displaySnackbar("success", "Deleted chat room successfully");

      updateQuery((prev) => ({
        chatRoomList: {
          data: prev.chatRoomList.data.filter((chatRoom) => chatRoom.id !== data.deleteChatRoom.id),
          total: prev.chatRoomList.total - 1,
        },
      }));
    },
    onError: (error) => {
      displaySnackbar("error", error.message);
    },
  });

  const handleCreateChatRoom = async (name: string) => {
    try {
      await createChatRoom({ variables: { name } });

      return true;
    } catch (error) {
      return false;
    }
  };

  const handleEditChatRoom = async (id: string, name: string) => {
    await updateChatRoom({
      variables: { id, name },
    });
  };

  const handleDeleteChatRoom = (id: string) => {
    deleteChatRoom({
      variables: { id },
    });
  };

  const chatRooms = chatRoomListData?.chatRoomList.data ?? [];

  return (
    <Paper sx={{ width: "100%", maxWidth: 400 }}>
      <Grid container style={{ padding: "0 20px" }}>
        <Grid size={8}>
          <Typography variant="h4">Chat Rooms</Typography>
        </Grid>

        <Grid size={4}>
          <ChatRoomFormDialog
            onSubmit={handleCreateChatRoom}
            buttonProps={{
              style: {
                margin: "15px 0",
                float: "right",
              },
              color: "primary",
              size: "small",
            }}
          />
        </Grid>
      </Grid>
      <Divider></Divider>
      <Box
        sx={{
          width: "100%",
          height: "80vh",
        }}
      >
        <AutoSizer>
          {({ height, width }) => (
            <FixedSizeList
              width={width}
              height={height}
              itemData={chatRooms}
              itemSize={80}
              itemCount={chatRooms.length}
              overscanCount={5}
            >
              {(listProps) => (
                <ChatRoomListRow
                  {...listProps}
                  chatRooms={chatRooms}
                  chatRoomId={chatRoomId}
                  onEditChatRoom={handleEditChatRoom}
                  onDeleteChatRoom={handleDeleteChatRoom}
                />
              )}
            </FixedSizeList>
          )}
        </AutoSizer>
      </Box>
    </Paper>
  );
};
